"use client";
"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Dropdown = void 0;
const react_1 = __importDefault(require("react"));
const foundations_1 = require("../../foundations");
const Icon_1 = require("../../foundations/IconV2/Icon");
const TextV2_1 = require("../../foundations/Typography/TextV2");
const hooks_1 = require("../../hooks");
const DropdownMenu_1 = require("./DropdownMenu");
const Dropdown = (_a) => {
    var { label, dropdownOptions, defaultOption, showNoResults = false, noResultsMessage = "No results found", error, description, disabled = false, readOnly = false, required = false, onSelectOption, onBlur } = _a, rest = __rest(_a, ["label", "dropdownOptions", "defaultOption", "showNoResults", "noResultsMessage", "error", "description", "disabled", "readOnly", "required", "onSelectOption", "onBlur"]);
    const dropdownRef = react_1.default.useRef(null);
    const [isDropdownVisible, setIsDropdownVisible] = react_1.default.useState(false);
    const [selectedOption, setSelectedOption] = react_1.default.useState(defaultOption);
    (0, hooks_1.useClickOutside)(dropdownRef, () => setIsDropdownVisible(false));
    const handleDropdownToggle = react_1.default.useCallback(() => {
        if (!disabled && !readOnly) {
            setIsDropdownVisible((prev) => !prev);
        }
    }, [disabled, readOnly]);
    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        onSelectOption === null || onSelectOption === void 0 ? void 0 : onSelectOption(option);
        setIsDropdownVisible(false);
    };
    const handleBlur = react_1.default.useCallback((event) => {
        if (dropdownRef.current &&
            !dropdownRef.current.contains(event.relatedTarget)) {
            setIsDropdownVisible(false);
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
        }
    }, [onBlur]);
    const handleContainerKeyDown = (event) => {
        switch (event.key) {
            case "Enter":
                event.preventDefault();
                setIsDropdownVisible((prev) => !prev);
                break;
            default:
                break;
        }
    };
    const shouldShowLabel = isDropdownVisible || !!selectedOption;
    const labelTextStyles = react_1.default.useMemo(() => {
        const labelYPosition = shouldShowLabel ? "-24px" : "-50%";
        const labelScale = shouldShowLabel ? 0.66 : 1;
        return {
            position: "absolute",
            left: "spacing-4",
            top: "50%",
            transform: `translateY(${labelYPosition}) scale(${labelScale})`,
            transformOrigin: "left center",
            transition: "easeOutFast",
            pointerEvents: "none",
            fontSize: "font-size-1",
            lineHeight: "leading-2",
            letterSpacing: "kerning-2",
            color: "text-incidental",
            whiteSpace: "nowrap",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            gap: "spacing-1"
        };
    }, [shouldShowLabel]);
    const inputStyles = react_1.default.useMemo(() => (Object.assign(Object.assign({ position: "relative", width: "100%", height: "spacing-10", px: "spacing-4", paddingTop: shouldShowLabel ? "spacing-7" : "spacing-5", paddingBottom: shouldShowLabel ? "spacing-3" : "spacing-5", borderRadius: "radius-2", cursor: disabled || readOnly ? "not-allowed" : "pointer" }, (disabled && { pointerEvents: "none" })), { display: "flex", alignItems: "center" })), [disabled, readOnly, shouldShowLabel]);
    const dropdownBorderStyles = react_1.default.useMemo(() => (Object.assign(Object.assign(Object.assign({ position: "relative", border: "1px solid", borderColor: error ? "input-stroke-error" : "input-stroke-enabled", borderRadius: "radius-2", marginBottom: isDropdownVisible ? "spacing-1" : "spacing-none", cursor: disabled || readOnly ? "not-allowed" : "pointer", backgroundColor: disabled || readOnly ? "feedback-surface-disabled" : "input-onSurface" }, (readOnly && {
        borderWidth: "0 0 1px 0",
        borderRadius: "unset"
    })), (isDropdownVisible && {
        outline: "2px solid",
        outlineColor: "focus",
        borderColor: "transparent"
    })), (!(disabled || readOnly) && {
        ":focus": {
            outline: "2px solid",
            outlineColor: "focus",
            borderColor: "transparent"
        }
    }))), [error, isDropdownVisible, disabled, readOnly]);
    return (react_1.default.createElement(foundations_1.Box, Object.assign({ sx: { display: "flex", flexDirection: "column" } }, rest),
        react_1.default.createElement(foundations_1.Box, { ref: dropdownRef, sx: { position: "relative", userSelect: "none" } },
            react_1.default.createElement(foundations_1.Box, { role: "button", sx: dropdownBorderStyles, tabIndex: disabled ? -1 : 0, "aria-disabled": disabled, "aria-haspopup": "listbox", "aria-expanded": isDropdownVisible, onClick: handleDropdownToggle, onBlur: handleBlur, onKeyDown: handleContainerKeyDown },
                react_1.default.createElement(foundations_1.Box, { as: "label", sx: labelTextStyles },
                    react_1.default.createElement(TextV2_1.TextV2, { variant: "body-1-regular" }, label),
                    required && (react_1.default.createElement(TextV2_1.TextV2, { variant: "body-1-regular", sx: { color: "feedback-onSurface-incidental-critical" } }, "*"))),
                react_1.default.createElement(foundations_1.Box, { sx: inputStyles },
                    selectedOption && (react_1.default.createElement(TextV2_1.TextV2, { variant: "body-1-regular" }, selectedOption.label)),
                    react_1.default.createElement(foundations_1.Box, { sx: {
                            position: "absolute",
                            top: "50%",
                            right: "spacing-4",
                            transform: "translateY(-50%)",
                            display: "flex"
                        } },
                        react_1.default.createElement(Icon_1.IconV2, { icon: isDropdownVisible ? "caret-up" : "caret-down", size: "xs", color: "text-incidental" })))),
            react_1.default.createElement(DropdownMenu_1.DropdownMenu, { dropdownOptions: dropdownOptions, showNoResults: showNoResults, noResultsMessage: noResultsMessage, isOpen: isDropdownVisible, selectedOption: selectedOption, onOptionSelect: handleOptionSelect, onDismiss: () => setIsDropdownVisible(false) })),
        description && (react_1.default.createElement(TextV2_1.TextV2, { variant: "body-2-regular", sx: { mt: "spacing-2", color: "text-incidental" } }, description)),
        error && (react_1.default.createElement(TextV2_1.TextV2, { variant: "body-2-regular", sx: { mt: "spacing-2", color: "input-stroke-error" } }, error))));
};
exports.Dropdown = Dropdown;
//# sourceMappingURL=Dropdown.js.map